import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import {
  FileExtensionResult,
  WorkSpaceNode,
  getWorkSpace,
  listFileExtension,
} from 'stores/WorkSpaceStoreAdapter';
import { errorDefault } from 'utils/Toastify';

type Props = {
  open: boolean;
  onClose: () => void;
  /** ファイル選択時に呼ばれる（fileId / ファイル名 / 拡張子 / 絶対パス） */
  onSelect: (file: { fileId: string; name: string; extension: string; path: string }) => void;
  /** ダイアログタイトル */
  title?: string;
  /**
   * 選択可能な拡張子を絞る条件。
   * - 'runnable': 実行可能拡張子のみ（code≠null）
   * - string[]: 指定拡張子のみ（小文字比較）
   * - undefined: 全ファイル選択可
   */
  filter?: 'runnable' | string[];
};

/** 拡張子を取り出す（小文字） */
const extractExt = (name: string): string => {
  const idx = name.lastIndexOf('.');
  return idx === -1 ? '' : name.slice(idx + 1).toLowerCase();
};

type FlatItem = {
  node: WorkSpaceNode;
  depth: number;
};

/**
 * ワークスペースツリー全体を表示し、ファイルを選択させるダイアログ。
 * 正解コード（実行可能拡張子）や knz などの選択に使う。
 * ツリーは常に全体を見せ、選択可否は filter で制御する（非対応はグレーアウト・二重防御）。
 */
const WorkspaceFileSelectDialog: React.FC<Props> = ({
  open,
  onClose,
  onSelect,
  title = 'ファイルを選択',
  filter,
}) => {
  const [nodes, setNodes] = React.useState<WorkSpaceNode[]>([]);
  const [extensions, setExtensions] = React.useState<FileExtensionResult[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([getWorkSpace(), listFileExtension()])
      .then(([ns, exts]) => {
        setNodes(ns);
        setExtensions(exts);
      })
      .catch(() => errorDefault('ワークスペースの取得に失敗しました'))
      .finally(() => setLoading(false));
  }, [open]);

  /** 実行可能拡張子集合（code≠null） */
  const runnableExts = React.useMemo(
    () => new Set(extensions.filter((e) => e.code != null).map((e) => e.extension)),
    [extensions],
  );

  /** ファイルが選択可能か */
  const isSelectable = React.useCallback(
    (node: WorkSpaceNode): boolean => {
      if (node.type !== 'FILE') return false;
      const ext = extractExt(node.name);
      if (!filter) return true;
      if (filter === 'runnable') return runnableExts.has(ext);
      return filter.map((f) => f.toLowerCase()).includes(ext);
    },
    [filter, runnableExts],
  );

  /** ツリーをフラット化（フォルダ→子）してインデント表示 */
  const flat = React.useMemo(() => {
    const byParent = new Map<string | null, WorkSpaceNode[]>();
    nodes.forEach((n) => {
      const key = n.parentId;
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key)!.push(n);
    });
    // フォルダ→ファイル、名前順
    byParent.forEach((arr) =>
      arr.sort((a, b) => {
        if (a.type !== b.type) return a.type === 'FOLDER' ? -1 : 1;
        return a.name.localeCompare(b.name);
      }),
    );
    const result: FlatItem[] = [];
    const walk = (parentId: string | null, depth: number) => {
      (byParent.get(parentId) ?? []).forEach((node) => {
        result.push({ node, depth });
        if (node.type === 'FOLDER') walk(node.id, depth + 1);
      });
    };
    walk(null, 0);
    return result;
  }, [nodes]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        elevation: 0,
        sx: { borderRadius: 3, border: 1, borderColor: 'divider' },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'auto',
            maxHeight: '55vh',
            minHeight: 200,
          }}
        >
          {loading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                読み込み中...
              </Typography>
            </Box>
          ) : flat.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                ファイルがありません
              </Typography>
            </Box>
          ) : (
            flat.map(({ node, depth }) => {
              const selectable = isSelectable(node);
              const isFolder = node.type === 'FOLDER';
              return (
                <Box
                  key={node.id}
                  onClick={() => {
                    if (!isFolder && selectable && node.fileId) {
                      onSelect({
                        fileId: node.fileId,
                        name: node.name,
                        extension: extractExt(node.name),
                        path: node.path ?? `/${node.name}`,
                      });
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    pl: 1.5 + depth * 2,
                    cursor: !isFolder && selectable ? 'pointer' : 'default',
                    opacity: isFolder || selectable ? 1 : 0.4,
                    transition: 'background-color 0.15s',
                    '&:hover': {
                      bgcolor: !isFolder && selectable ? 'action.hover' : 'transparent',
                    },
                  }}
                >
                  {isFolder ? (
                    <FolderOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  ) : selectable ? (
                    <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  ) : (
                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{ color: isFolder ? 'text.secondary' : 'text.primary' }}
                  >
                    {node.name}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>
        <Stack sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {filter === 'runnable'
              ? '実行可能なファイル（コードファイル）のみ選択できます'
              : Array.isArray(filter)
                ? `対応拡張子（${filter.join(' / ')}）のファイルのみ選択できます`
                : 'ファイルを選択してください'}
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default WorkspaceFileSelectDialog;
